import React, { ChangeEvent, DragEventHandler, useState } from 'react';
import styled from 'styled-components';
// --------------------------------------------------------------------------------
import { String } from '../String';
import { Button } from '../Button';
import { InputProps } from '../Input';
import { TargetProps } from './SelectDropDown';
import { config } from '../../index';

export interface FileUploadProps extends InputProps {
  /* FileUpload state */
  checked?: boolean;
  /* FileUpload margin */
  margin?: string;
  /* FileUpload padding */
  padding?: string;
  /* FileUpload max width */
  maxWidth?: string;
  /* FileUpload min width */
  minWidth?: string;
  /* File upload blob | boolean */
  hasFiles?: boolean | TargetProps;
  /* Allow multiple file upload */
  alowMultiple?: boolean;
  // --------------------------------------------------------------------------------
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | TargetProps>
  ) => void;
}

const FileUploadWrapper = styled.div<FileUploadProps>`
  display: flex;
  width: 100%;
  align-items: center;
  border-radius: ${(props) => props.borderRadius};
  height: ${(props) => props.height};
  max-width: ${(props) => props.maxWidth};
  min-width: ${(props) => props.minWidth};
  background-color: ${(props) =>
    props.hasFiles ? props.backgroundColor : config.muted};
  transition: ${config.transitionShort};
  margin: ${(props) => props.margin};
  cursor: pointer;
  user-select: none;
`;

// 📌 Primary UI component for user interaction
export const FileUpload: React.FC<FileUploadProps> = ({
  label,
  name = 'fileUpload',
  // --------------------------------------------------------------------------------
  maxWidth,
  minWidth,
  height = config.spacingXXLarge,
  borderRadius = config.spacingXSmall,
  margin,
  padding,
  backgroundColor = config.meadow,
  border = `1px solid ${config.muted}`,
  alowMultiple = false,
  // --------------------------------------------------------------------------------
  onChange,
  // additional props
  ...props
}) => {
  const [hasFiles, setHasFiles] = useState<TargetProps | undefined>();

  // --------------------------------------------------------------------------------
  // 📌  File name display for user interaction
  // --------------------------------------------------------------------------------
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    'File Uploaded. Drag and drop files to upload.'
  );

  // --------------------------------------------------------------------------------
  // 📌  Create a reference to the hidden file input element
  // --------------------------------------------------------------------------------
  const hiddenFileInput = React.useRef<HTMLInputElement>(null);

  // Programmatically click the hidden file input element when the Button component is clicked
  const handleInputClick = () => {
    hiddenFileInput?.current?.click();
  };

  const handleFileUploadChange = (files: any) => {
    // if multiple files are allow pass through otherwise just the first file
    const fileContent = alowMultiple ? files : files[0];
    const type = files[0].type;

    setHasFiles(fileContent);
    onChange?.({
      target: {
        value: fileContent,
        name: name || label?.string || '', // if no name is provided use the label
        type,
      },
      isTrusted: true,
    } as any);

    if (!fileContent) return;
    let fileLength = alowMultiple ? files.length : 1;
    // update error messages based in file upload
    if (fileLength === 1)
      setErrorMessage(`${fileContent.name || files[0].name} file uploaded.`);
    // if multiple files are uploaded, display error message
    if (fileLength > 1) setErrorMessage(`${fileLength} file(s) uploaded.`);
  };

  // Call a function (passed as a prop from the parent component) to handle the user-selected file
  const handleFileUpload = (event: ChangeEvent<HTMLInputElement> | any) => {
    const files = event?.target.files;

    // update state with file upload changes
    if (files) handleFileUploadChange(files);
  };

  // TODO event: DragEvent throws TS error
  const drop = (event: DragEventHandler<HTMLDivElement> | any) => {
    event.stopPropagation();
    event.preventDefault();

    const dt = event.dataTransfer;
    const files = dt?.files;

    // update state with file upload changes
    if (files) handleFileUploadChange(files);
  };

  return (
    <FileUploadWrapper
      margin={margin}
      padding={padding}
      maxWidth={maxWidth}
      minWidth={minWidth}
      height={height}
      backgroundColor={hasFiles ? backgroundColor : config.muted}
      border={border}
      borderRadius={borderRadius}
      hasFiles={hasFiles}
      // --------------------------------------------------------------------------------
      // file upload handlebars to the drop pane for the file upload
      draggable="true"
      onDragOver={(event) => event.preventDefault()}
      onDrop={drop}
      // --------------------------------------------------------------------------------
      onClick={handleInputClick}
      {...props}
    >
      <Button
        label={{
          string: 'File Upload',
          fontSize: config.fontSmall,
        }}
        padding={`${config.spacingSmall} ${config.spacingSmall}`}
      />
      <String
        color={hasFiles ? config.white : config.darkMuted}
        fontSize={config.fontXSmall}
        padding={`0 ${config.spacingXMedium}`}
        fontWeight="400"
        opacity={hasFiles ? 1 : 0.5}
        limit={1}
        cursor="pointer"
        // --------------------------------------------------------------------------------
        {...label}
        string={errorMessage}
      />

      <input
        type="file"
        ref={hiddenFileInput}
        onChange={handleFileUpload}
        style={{ display: 'none' }}
        multiple={alowMultiple}
      />
    </FileUploadWrapper>
  );
};
